import { Star } from 'lucide-react'
import { cn } from '../lib/cn'

/** Maps the Figma `<Star-group>` / `<Star-rating>` / `<Star-Single>` sets. */
const sizes = { xs: 'size-3', sm: 'size-3.5', md: 'size-4', lg: 'size-5' } as const

export type RatingProps = {
  /** 0-5, halves allowed. */
  value: number
  count?: number | undefined
  size?: keyof typeof sizes
  showValue?: boolean
  className?: string
}

export function Rating({ value, count, size = 'md', showValue = true, className }: RatingProps) {
  const clamped = Math.max(0, Math.min(5, value))
  const label =
    count === undefined
      ? `${clamped.toFixed(1)} sur 5`
      : `${clamped.toFixed(1)} sur 5, ${count} avis`
  return (
    <span
      className={cn('inline-flex items-center gap-1.5', className)}
      role="img"
      aria-label={label}
    >
      <span className="inline-flex" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => {
          const fill = Math.max(0, Math.min(1, clamped - i))
          return (
            <span key={i} className="relative inline-block">
              <Star className={cn(sizes[size], 'text-slate-300')} />
              {fill > 0 ? (
                <span
                  className="absolute inset-0 overflow-hidden"
                  style={{ width: `${fill * 100}%` }}
                >
                  <Star className={cn(sizes[size], 'fill-warning text-warning')} />
                </span>
              ) : null}
            </span>
          )
        })}
      </span>
      {showValue ? (
        <span className="text-xs font-medium text-slate-700" aria-hidden="true">
          {clamped.toFixed(1)}
        </span>
      ) : null}
      {count !== undefined ? (
        <span className="text-xs text-slate-500" aria-hidden="true">
          ({count})
        </span>
      ) : null}
    </span>
  )
}
